import { Dropdown, Stack, Text, Toggle } from "@fluentui/react";
import { useCallback, useMemo } from "react";
import {
  chartHeightDropdownStyle,
  chartHeightStackStyle,
  disabledToggleStyle,
  dropdownStyle,
  featureStackStyle,
  toggleStackStyle
} from "../ExploreFeatures.styles";
import { useFeatureSelection } from "../Hooks/useFeatureSelection.hook";
import { FeatureControlsProps } from "../ExploreFeatures.interfaces";
import { CHART_HEIGHT_OPTIONS, DIVIDER_KEY } from "../../../components/CustomerView/CustomerView.constants";

export const FeatureControls = ({
  rawFeatureData,
  selectedFeatures,
  setSelectedFeatures,
  chartHeight,
  onChartHeightChange,
  showDataTable,
  onDataTableToggle,
  disableDataTableToggle,
  customProps
}: FeatureControlsProps) => {
  const { featureOptions, selectedKeys, handleFeatureSelection } = useFeatureSelection(
    rawFeatureData,
    selectedFeatures,
    setSelectedFeatures,
    customProps
  );

  // Memoize filtered options and allSelected calculation
  const featureOptionsWithoutDivider = useMemo(
    () => featureOptions.filter((opt) => opt.key !== DIVIDER_KEY),
    [featureOptions]
  );

  const allSelected = useMemo(
    () => selectedKeys.length === featureOptionsWithoutDivider.length && featureOptionsWithoutDivider.length > 0,
    [selectedKeys, featureOptionsWithoutDivider]
  );

  const onRenderTitle = useCallback(
    (options?: any[]) => {
      if (allSelected) return <span>Select All</span>;
      if (!options || options.length === 0) return <span>Select features to display</span>;
      return <span>{options.map((o) => o.text).join(", ")}</span>;
    },
    [allSelected]
  );

  return (
    <Stack tokens={{ childrenGap: 10 }}>
      <Stack horizontal tokens={{ childrenGap: 15 }}>
        <Stack style={featureStackStyle}>
          <Dropdown
            placeholder="Select features to display"
            label="Features"
            multiSelect
            options={featureOptions}
            selectedKeys={selectedKeys}
            onChange={handleFeatureSelection}
            styles={dropdownStyle}
            onRenderTitle={onRenderTitle}
          />
        </Stack>

        <Stack style={chartHeightStackStyle}>
          <Dropdown
            label="Chart Height"
            selectedKey={chartHeight}
            options={CHART_HEIGHT_OPTIONS}
            onChange={onChartHeightChange}
            styles={chartHeightDropdownStyle}
          />
        </Stack>

        <Stack style={toggleStackStyle}>
          <Toggle
            label="Show Tabular View"
            checked={showDataTable}
            onChange={onDataTableToggle}
            disabled={disableDataTableToggle}
            styles={disableDataTableToggle ? disabledToggleStyle : undefined}
          />
        </Stack>
      </Stack>

      {selectedFeatures.length > 0 && (
        <Text variant="small">
          Showing {selectedFeatures.length} of {rawFeatureData.length} features
        </Text>
      )}
    </Stack>
  );
};
